import { Text, TextLink } from '@alternative/acqua'

import { useCompanyAndInvoiceContext } from '@/hooks/providers/CompanyAndInvoiceProvider'
import BaseButton from '@/ui/Button/v2'

import { sigInAndPayButton } from './styles'

/**
 * Shown instead of "Sign in" when the visitor is already signed in but their
 * account has no active membership in this portal (see
 * `useActiveUserInfo().hasNoPortalMembership`). Offering "Sign in" again here
 * only sends them through the login round-trip and back to the same screen.
 */
const NoPortalMembershipNotice = ({
  onContinueAsGuest
}: {
  /** Omitted where guest checkout isn't available (custom invoices, payment requests). */
  onContinueAsGuest?: () => void
}) => {
  const { company, invoice } = useCompanyAndInvoiceContext()
  const partnerName = company?.name || 'the company that sent this invoice'
  const customerName = invoice?.customer?.name

  return (
    <div className='mt-6 flex flex-col gap-4' role='status'>
      <Text variant='body-lg' weight='semibold' as='p'>
        Your account doesn&apos;t have access to this invoice
      </Text>

      <Text variant='body-md' as='p' className='text-surface-body'>
        You&apos;re signed in, but your account isn&apos;t linked to
        {customerName ? ` ${customerName}` : ' this customer'} in {partnerName}
        &apos;s payment portal.{' '}
        {onContinueAsGuest
          ? 'You can still pay this invoice as a guest, or ask them to give your account access.'
          : 'Ask them to give your account access.'}
      </Text>

      {onContinueAsGuest && (
        // Same button as "Sign in and pay" so the card keeps one primary style.
        <BaseButton
          styles={{
            ...sigInAndPayButton,
            marginTop: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
          onClick={onContinueAsGuest}
        >
          Continue as guest
        </BaseButton>
      )}

      <Text variant='body-md' as='p' className='text-center'>
        <TextLink variant='body-md' href='/logout'>
          Sign in with a different account
        </TextLink>
      </Text>
    </div>
  )
}

export default NoPortalMembershipNotice
